/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from "react";
import { useFormState, Controller } from "react-hook-form";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import moment from "moment";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";

import {
  generateFormFieldRules,
  getParsedValue,
} from "../services/form.service";
import {
  getRelativeDefaultDate,
  getValidationDate,
} from "../services/form.date.service";

function FormDateField(props) {
  const {
    fieldProps,
    formProps,
    control,
    disabled,
    resetHandler,
    trigger,
    getValues,
    setValue,
  } = props;
  const { key, label, validate, defaultDate } = fieldProps;
  const showDateAndTime = key.endsWith("-dtm");
  const { errors } = useFormState({ control, name: key });
  const [dateError, setDateError] = useState(null);
  const condition = validate?.custom && getParsedValue(validate.custom, key);
  const updateOnLoad = condition?.updateOnLoad || false;
  const disablePaste = condition?.disablePaste || false;

  let initialValue = formProps[key] || null;
  if (defaultDate === "today" && (!formProps[key] || updateOnLoad)) {
    if (showDateAndTime) initialValue = moment().toISOString();
    else initialValue = moment().format("MM/DD/YYYY");
  } else if (defaultDate && (!formProps[key] || updateOnLoad)) {
    // relative defaults ex: "1year", "+6months", "-18years", "30days"
    const relativeDate = getRelativeDefaultDate(defaultDate);
    if (relativeDate) {
      if (showDateAndTime) initialValue = moment(relativeDate).toISOString();
      else initialValue = moment(relativeDate).format("MM/DD/YYYY");
    }
  }

  const errorMsg = {
    minDate: condition?.minDateMessage || "Please select later date",
    maxDate: condition?.maxDateMessage || "Please select former date",
    invalidDate: "Invalid date",
    dob: "Age of applicant is not within acceptable range.",
    expectedDate: "Cannot be same as licensed date",
  };

  const triggerValidation = () => {
    setTimeout(() => trigger(key), 5);
  };

  const handleDateChange = (dateObj, onChange) => {
    let date = null;
    if (dateObj && dateObj.$d && dateObj.isValid()) {
      if (showDateAndTime) date = moment(dateObj.$d).toISOString();
      else date = moment(dateObj.$d).format("MM/DD/YYYY");
    }
    onChange(date);
    resetHandler();
    triggerValidation();
  };

  const dateValidation = (type) => {
    const result = getValidationDate(condition, type);
    if (!result) return null;
    return dayjs(result);
  };

  const rules = generateFormFieldRules(fieldProps, getValues, dateError);
  React.useEffect(() => {
    if (formProps[key] === "" || updateOnLoad) {
      setTimeout(() => setValue(key, initialValue), 5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textFieldSlotProps = {
    variant: "standard",
    error: !!(errors[key] || dateError),
    "aria-describedby":
      [
        disablePaste ? `${label}-paste-disabled` : null,
        errors[key] || dateError ? `${fieldProps.key}-error` : null,
      ]
        .filter(Boolean)
        .join(" ") || undefined,
    onBlur: triggerValidation,
    required: validate?.required,
    onKeyDown: (e) => {
      if (disablePaste && (e.ctrlKey || e.metaKey)) {
        const _key = e.key.toLowerCase();
        if (_key === "v" || _key === "c" || _key === "x") {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    },
    onContextMenu: (e) => {
      if (disablePaste) e.preventDefault();
    },
  };

  return (
    <FormControl
      fullWidth
      margin="dense"
      sx={{ minHeight: "5.9375rem" }}
      disabled={disabled}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name={key}
          control={control}
          defaultValue={initialValue || null}
          render={({ field: { onChange, ref, value, ...field } }) =>
            !showDateAndTime ? (
              <DatePicker
                {...field}
                id={key}
                name={key}
                label={label}
                onChange={(dateObj) => handleDateChange(dateObj, onChange)}
                minDate={condition ? dateValidation("min") : undefined}
                maxDate={condition ? dateValidation("max") : undefined}
                onError={(error) => setDateError(error)}
                disabled={disabled}
                views={["year", "month", "day"]}
                value={value ? dayjs(value) : null}
                onClose={triggerValidation}
                slotProps={{ textField: textFieldSlotProps }}
                autoComplete={key}
              />
            ) : (
              <DateTimePicker
                {...field}
                id={key}
                name={key}
                label={label}
                onChange={(dateObj) => handleDateChange(dateObj, onChange)}
                minDate={condition ? dateValidation("min") : undefined}
                maxDate={condition ? dateValidation("max") : undefined}
                onError={(error) => setDateError(error)}
                disabled={disabled}
                value={value ? dayjs(value) : null}
                onClose={triggerValidation}
                slotProps={{ textField: textFieldSlotProps }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
                autoComplete={key}
              />
            )
          }
          rules={rules}
        />
      </LocalizationProvider>
      {(errors[key] || dateError) && (
        <FormHelperText error id={`${fieldProps.key}-error`} sx={{ margin: 0 }}>
          {(dateError && errorMsg[dateError]) ||
            errors[key]?.message ||
            "This is required."}
        </FormHelperText>
      )}
    </FormControl>
  );
}
FormDateField.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    validate: PropTypes.shape({
      required: PropTypes.bool,
      custom: PropTypes.string,
      customMessage: PropTypes.string,
    }),
    defaultDate: PropTypes.string,
  }).isRequired,
  formProps: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  getValues: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  resetHandler: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
};
export default FormDateField;
